// The task client's shell: who is signed in decides what shows (chapter 10). A valid user
// session opens the menu; an enrolled device without a shift shows the shift login; a plain
// browser shows the desktop login. The development settings (integrator token) still work
// as a fallback while enrolment is rolled out.

import { useEffect, useState } from "react";
import { adoptSession, refreshSession, Api, applyPrefs, applySetupPayload, Auth, authReady, effectiveSettings, getDevice, getSession, saveDevice, settingsReady } from "./api/client.js";
import { AdminShell } from "./admin/AdminShell.jsx";
import { t } from "./i18n.js";
import { Login, ShiftLogin, type PendingChoice } from "./Login.js";
import { setHealthScreen } from "./diag/health.js";
import { PhotoTest } from "./PhotoTest.js";
import { PutAway } from "./putaway/PutAway.js";
import { Count } from "./count/Count.jsx";
import { Pick } from "./pick/Pick.js";
import { Release } from "./pick/Release.js";
import { Ship } from "./pick/Ship.js";
import { Lookup } from "./lookup/Lookup.js";
import { attachWedge } from "./scan/wedge.js";
import { Move } from "./move/Move.js";
import { loadCachedBundle } from "./receive/registry.js";
import { ItemLabel } from "./receive/ItemLabel.js";
import { Receive } from "./receive/Receive.js";
import { pending, subscribe, type QueuedStep } from "./receive/queue.js";
import { ScanTest } from "./ScanTest.js";
import { unlockAudio } from "./scan/feedback.js";
import { Settings } from "./Settings.js";
import { Brand, ListItem, Strip, Actions, Btn } from "./ui/components.jsx";

type Screen = "menu" | "receive" | "returns" | "itemlabel" | "putaway" | "move" | "pick" | "release" | "ship" | "count" | "lookup" | "settings" | "scantest" | "admin";

export function App() {
  const [screen, setScreen] = useState<Screen>(() => (window.location.hash.startsWith("#/") && getSession()?.permissions.includes("users.manage") ? "admin" : "menu"));
  const [photoTest, setPhotoTest] = useState(() => new URLSearchParams(window.location.search).has("photo"));
  const [queue, setQueue] = useState<QueuedStep[]>(pending);
  const [online, setOnline] = useState(navigator.onLine);
  const [waiting, setWaiting] = useState<number | null>(null);
  const [pickQueue, setPickQueue] = useState<number | null>(null);
  const [rounds, setRounds] = useState<number | null>(null);
  const [deliveries, setDeliveries] = useState<number | null>(null);
  /** The menu by area (owner 2026-11-01): one button per area, the area's choices on the next screen. */
  const [area, setArea] = useState<"receiving" | "outbound" | null>(null);
  // Pick and release as two steps (company setting): Utleverans then has two choices.
  const twoStep = !!loadCachedBundle()?.tenantConfig?.erp?.report?.twoStepRelease;
  // A scan on the menu itself opens Slå upp with that code (owner 2026-10-29).
  const [lookupScan, setLookupScan] = useState<string | null>(null);
  // The hidden field is held in state, not a ref: on an enrolled phone the app starts on the
  // shift login with the screen already "menu", so an effect keyed on the screen alone ran
  // before the menu existed and never again (owner 2026-10-29: a scan on the menu did nothing).
  const [menuWedge, setMenuWedge] = useState<HTMLInputElement | null>(null);
  useEffect(() => {
    if (screen !== "menu" || !menuWedge) return;
    return attachWedge(menuWedge, { onScan: (d) => { setLookupScan(d); setScreen("lookup"); } });
  }, [screen, menuWedge]);
  const [tick, setTick] = useState(0); // re-evaluate auth after login/logout/enrolment
  // The stored session is checked against the server once at start: renewed expiry, or dropped when revoked.
  useEffect(() => { void refreshSession().then((r) => { if (r === "renewed" || r === "dropped") setTick((n) => n + 1); }); }, []);
  useEffect(() => setHealthScreen(screen), [screen]);
  const [notice, setNotice] = useState<string | undefined>(undefined);
  // A tenant choice handed back by the Entra callback (the address is a user in several).
  const [pendingChoice, setPendingChoice] = useState<PendingChoice | undefined>(undefined);

  // A new version that arrived while a task was open is applied the next time the operator stands in
  // the menu, where nothing is lost (owner 2026-11-02: the phone kept the old version through reloads).
  useEffect(() => {
    const w = window as unknown as { __trailApplyUpdate?: () => void };
    const apply = () => { if (screen === "menu" && w.__trailApplyUpdate) { const f = w.__trailApplyUpdate; delete w.__trailApplyUpdate; f(); } };
    apply();
    window.addEventListener("trail:update-waiting", apply);
    return () => window.removeEventListener("trail:update-waiting", apply);
  }, [screen]);
  useEffect(() => {
    if (screen === "menu" && authReady()) {
      Api.putAwayQueue().then((q) => setWaiting(q.length)).catch(() => setWaiting(null));
      Api.salesOrders().then((o) => setPickQueue(o.length)).catch(() => setPickQueue(null));
      Api.countRounds("open").then((r) => setRounds(r.length)).catch(() => setRounds(null));
      Api.listReceipts().then((r) => setDeliveries(r.filter((x) => x.status === "open" || x.status === "proposed").length)).catch(() => setDeliveries(null));
    }
  }, [screen, tick]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    // Legacy one-time setup link (development): /?setup=<base64 {token, tenant}>.
    const setup = params.get("setup");
    if (setup) {
      applySetupPayload(setup);
      params.delete("setup");
      setScreen("settings");
    }
    // Enrolment link from the administrator: /?enrol=<code> — exchanged for the device credential.
    const enrol = params.get("enrol");
    if (enrol) {
      params.delete("enrol");
      Auth.enrol(enrol)
        .then((d) => {
          saveDevice(d);
          setTick((x) => x + 1);
        })
        .catch(() => setNotice(t("enrol.failed")));
    }
    const login = params.get("login");
    if (login === "failed") setNotice(t("login.entraFailed"));
    if (login === "unknown-user") setNotice(t("login.unknown"));
    if (login === "password-only") setNotice(t("login.passwordOnly"));
    params.delete("login");
    // A session handed back by the Entra callback arrives in the fragment.
    const frag = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    const sessionToken = frag.get("session");
    if (sessionToken) {
      // A fresh login lands where the role says, not where the tab was last.
      sessionStorage.removeItem("trail.stayTask");
      adoptSession(sessionToken, frag.get("expires") ?? new Date(Date.now() + 3600_000).toISOString())
        .then(() => setTick((x) => x + 1))
        .catch(() => setNotice(t("login.entraFailed")));
    }
    const choose = frag.get("choose");
    const tenants = frag.get("tenants");
    if (choose && tenants) {
      try {
        const b64 = tenants.replace(/-/g, "+").replace(/_/g, "/");
        const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
        setPendingChoice({ choice: choose, tenants: JSON.parse(new TextDecoder().decode(Uint8Array.from(atob(padded), (c) => c.charCodeAt(0)))) as PendingChoice["tenants"] });
      } catch {
        setNotice(t("login.entraFailed"));
      }
    }
    if (setup || enrol || sessionToken || login || choose) {
      window.history.replaceState(null, "", `${window.location.pathname}${params.size ? `?${params}` : ""}`);
    }
    // The desktop login page is office-side (light); everything else here is the task client.
    applyPrefs(undefined, !getSession() && !getDevice() && window.innerWidth >= 840 ? "admin" : "task");
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    const unsub = subscribe(setQueue);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
      unsub();
    };
  }, []);

  if (photoTest) return <PhotoTest onClose={() => setPhotoTest(false)} />;
  if (screen === "scantest") return <ScanTest onClose={() => setScreen("settings")} />;
  if (screen === "settings") return <Settings onClose={() => { setScreen("menu"); setTick((x) => x + 1); }} onScanTest={() => setScreen("scantest")} />;

  const session = getSession();
  const device = getDevice();
  // An enrolled device always starts with a shift login: the device credential alone may not
  // run flows (R7.24), and the development shortcut must not bypass that.
  if (!session && device) return <ShiftLogin device={device} onDone={() => setTick((x) => x + 1)} onSettings={() => setScreen("settings")} />;
  if (!session && !settingsReady()) return <Login notice={notice} pending={pendingChoice} onDone={() => { setPendingChoice(undefined); setTick((x) => x + 1); }} onDevSettings={() => setScreen("settings")} />;

  const isAdmin = session?.permissions.includes("users.manage") || session?.permissions.includes("config.edit");
  // A fresh administrator session on a wide screen opens the administration surface.
  if (screen === "menu" && isAdmin && window.innerWidth >= 840 && !sessionStorage.getItem("trail.stayTask")) {
    setScreen("admin");
  }
  if (screen === "admin" && isAdmin) {
    return (
      <AdminShell
        onTaskClient={() => {
          sessionStorage.setItem("trail.stayTask", "1");
          window.location.hash = "";
          setScreen("menu");
          setTick((x) => x + 1);
        }}
      />
    );
  }
  if (screen === "receive") return <Receive onClose={() => setScreen("menu")} />;
  if (screen === "returns") return <Receive mode="return" onClose={() => { setArea("receiving"); setScreen("menu"); }} />;
  if (screen === "itemlabel") return <ItemLabel onClose={() => { setArea("receiving"); setScreen("menu"); }} />;
  if (screen === "putaway") return <PutAway onClose={() => setScreen("menu")} />;
  if (screen === "count") return <Count onClose={() => setScreen("menu")} />;
  if (screen === "pick") return <Pick onClose={() => { setArea(twoStep ? "outbound" : null); setScreen("menu"); }} />;
  if (screen === "release") return <Release onClose={() => { setArea("outbound"); setScreen("menu"); }} />;
  if (screen === "ship") return <Ship onClose={() => { setArea("outbound"); setScreen("menu"); }} />;
  if (screen === "lookup") return <Lookup initial={lookupScan ?? undefined} onClose={() => { setLookupScan(null); setScreen("menu"); }} />;
  if (screen === "move") return <Move onClose={() => setScreen("menu")} />;

  const who = session ? session.name : effectiveSettings().actorId;
  // Flytta is a role setting (site config); a device without a user session (development) sees it.
  const moveRoles = loadCachedBundle()?.siteConfig?.move?.roles ?? ["operator", "lead", "quality", "administrator"];
  const canMove = !session || (session.roles ?? []).some((r) => moveRoles.includes(r)) || session.permissions.includes("config.edit");
  const stuck = queue.filter((q) => q.lastError && !/fetch|network|abort|5\d\d|401|429/i.test(q.lastError)).length;
  // The menu is configuration (ground rule 2026-10-31): the receiving area's choices follow the site's
  // enabled flows, and an area with exactly one enabled choice opens it directly (owner 2026-11-01).
  const enabledFlows = loadCachedBundle()?.siteConfig?.receiving?.enabledFlows ?? ["receive-pallet"];
  const receivingChoices: { flow: string; screen: "receive" | "putaway"; built: boolean }[] = [
    { flow: "receive-pallet", screen: "receive", built: true },
    { flow: "receive-to-bin", screen: "receive", built: false },
    { flow: "receive-supplier-pallet", screen: "receive", built: false },
  ];
  const receivingEnabled: ("receive" | "putaway")[] = [...receivingChoices.filter((c) => c.built && enabledFlows.includes(c.flow)).map((c) => c.screen), "putaway"];
  const receivingDesc = [deliveries ? t("menu.area.receiving.deliveries", { n: deliveries }) : null, waiting ? t("menu.area.receiving.waiting", { n: waiting }) : null].filter(Boolean).join(" · ") || t("menu.area.receiving.desc");
  const openReceiving = () => (receivingEnabled.length === 1 ? setScreen(receivingEnabled[0]!) : setArea("receiving"));
  return (
    <div className="screen" onClick={unlockAudio}>
      <input ref={setMenuWedge} readOnly tabIndex={-1} aria-hidden="true" style={{ position: "absolute", width: 0, height: 0, opacity: 0, pointerEvents: "none" }} />
      <Strip left={<Brand />} middle={who} online={online} queued={queue.length} stuck={stuck} />
      <div className="body">
        {area === null && (
          <div className="list big">
            <ListItem icon="pallet" title={t("menu.area.receiving")} desc={receivingDesc} onClick={openReceiving} />
            <ListItem icon="pallet" title={t("menu.outbound")} desc={pickQueue !== null ? t("menu.pick.queue", { n: pickQueue }) : t("menu.pick.desc")} onClick={() => (twoStep ? setArea("outbound") : setScreen("pick"))} />
            {canMove && <ListItem icon="bin" title={t("menu.move")} desc={t("menu.move.desc")} onClick={() => setScreen("move")} />}
            <ListItem icon="bin" title={t("menu.count")} desc={rounds ? t("menu.count.rounds", { n: rounds }) : t("menu.count.desc")} onClick={() => setScreen("count")} />
            <ListItem icon="gear" title={t("menu.settings")} desc={t("menu.settings.desc")} onClick={() => setScreen("settings")} />
            {isAdmin && <ListItem icon="test" title={t("adm.toAdmin")} desc={t("adm.toAdmin.desc")} onClick={() => { sessionStorage.removeItem("trail.stayTask"); setScreen("admin"); }} />}
            <p className="hint" style={{ marginTop: 12 }}>{t("menu.scanHint")} <button type="button" className="chip" onClick={() => setScreen("lookup")}>{t("menu.lookup")}</button></p>
          </div>
        )}
        {area === "receiving" && (
          <>
            <h2 className="sec">{t("menu.area.receiving")}</h2>
            <div className="list">
              {enabledFlows.includes("receive-pallet") ? <ListItem icon="pallet" title={t("menu.pallet")} desc={deliveries ? t("menu.pallet.deliveries", { n: deliveries }) : t("menu.pallet.desc")} onClick={() => setScreen("receive")} /> : <ListItem icon="pallet" title={t("menu.pallet")} desc={t("menu.pallet.desc")} off tag={t("menu.notEnabled")} />}
              <ListItem icon="bin" title={t("menu.toBin")} desc={t("menu.toBin.desc")} off tag={enabledFlows.includes("receive-to-bin") ? t("menu.soon") : t("menu.notEnabled")} />
              <ListItem icon="pallet" title={t("menu.supplierPallet")} desc={t("menu.supplierPallet.desc")} off tag={enabledFlows.includes("receive-supplier-pallet") ? t("menu.soon") : t("menu.notEnabled")} />
              <ListItem icon="putaway" title={t("menu.putaway")} desc={waiting !== null ? t("menu.putaway.waiting", { n: waiting }) : t("menu.putaway.desc")} onClick={() => setScreen("putaway")} />
              <ListItem icon="pallet" title={t("menu.returns")} desc={t("menu.returns.desc")} onClick={() => setScreen("returns")} />
              <ListItem icon="scan" title={t("menu.itemLabel")} desc={t("menu.itemLabel.desc")} onClick={() => setScreen("itemlabel")} />
            </div>
          </>
        )}
        {area === "outbound" && (
          <>
            <h2 className="sec">{t("menu.outbound")}</h2>
            <div className="list">
              <ListItem icon="pallet" title={t("menu.pick")} desc={pickQueue !== null ? t("menu.pick.queue", { n: pickQueue }) : t("menu.pick.desc")} onClick={() => setScreen("pick")} />
              <ListItem icon="bin" title={t("menu.release")} desc={t("menu.release.desc")} onClick={() => setScreen("release")} />
              <ListItem icon="putaway" title={t("menu.ship")} desc={t("menu.ship.desc")} onClick={() => setScreen("ship")} />
            </div>
          </>
        )}
      </div>
      {area !== null && <Actions><Btn kind="quiet" icon="back" onClick={() => setArea(null)}>{t("common.menu")}</Btn></Actions>}
    </div>
  );
}
